"use client";
import clsx from "clsx";
import { motion } from "framer-motion";
import React, { ReactNode } from "react";
import { CheckCircleIcon } from "@heroicons/react/24/solid";

interface StepperProps {
  steps: {
    icon?: ReactNode;
    activeIcon?: ReactNode;
    title: string;
    description?: ReactNode | string;
  }[];
  active?: number;
}

export function Stepper({ steps, active = 0 }: StepperProps) {
  return (
    <ul role="list" className="space-y-6">
      {steps.map((step, index) => {
        const isCompleted = index < active;
        const isCurrent = index === active;
        const isPast = index < active;
        return (
          <li key={index} className="relative flex gap-x-3">
            <div
              className={clsx(
                steps.length === index + 1 ? "h-6" : "-bottom-6",
                "absolute left-0 top-0 flex w-10 justify-center",
              )}>
              {index !== 0 && (
                <div
                  className={clsx(
                    "w-px h-full",
                    isPast ? "bg-dark h-full" : "bg-gray-300 h-full",
                  )}
                />
              )}
              {isCurrent && (
                <motion.div
                  initial={{ height: "100%" }}
                  animate={{ height: "100%" }}
                  transition={{ duration: 0.5 }}
                  className="absolute w-px bg-dark h-full"
                />
              )}
              {(isPast || active == -1) && (
                <div className="absolute w-px bg-dark h-full" />
              )}
            </div>
            <div className="relative flex h-8 w-10 flex-none items-center justify-center bg-white">
              {isCompleted ? (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5 }}>
                  <CheckCircleIcon className="text-dark rounded-full border size-7 border-dark bg-[#F9EE20] p-[3px]" />
                </motion.div>
              ) : (
                <div className="text-dark rounded-full border size-5 border-dark p-[3px]" />
              )}
            </div>
            <div>
              <p
                className={`flex-auto leading-5  ${
                  isCurrent || isPast
                    ? "text-dark font-semibold"
                    : "text-gray-500 font-medium"
                } text-base`}>
                {step.title}
              </p>
              <p className="pt-1 text-gray-500">{step.description}</p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
